import React, { FunctionComponent, useState } from 'react';
import { FabChecklist } from '@campfire/fab-checklist';
import { AppBar as MuiAppBar, Box, Button } from '@material-ui/core';
import pluralize from 'pluralize';
import { AccessTimeOutlined } from '@material-ui/icons';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import { DateTime, Interval } from 'luxon';

import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { AppBarAvatarMenu } from './AppBarAvatarMenu';
import { CampfireHeader } from '../../components';
import { useOnboardingContext } from '../../onboarding/useOnboardingContext';
import { AvailabilityCenter } from '../../components/AvailabilityCenter/AvailabilityCenter';
import { useOrgInfo } from '../../auth/use-org-info/use-org-info';
import { useUser } from '../../auth/useUser';
import { TrialUpgradeDialog } from './TrialUpgradeDialog';

interface Props {
  isDrawerOpen: boolean;
  toggleDrawerOpen: () => void;
}

const CampfireAppBar: FunctionComponent<Props> = ({ toggleDrawerOpen, isDrawerOpen }) => {
  const { theme, isMobile } = useCampfireTheme();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const FabChecklistWrapper = () => {
    const { currentOnboardingStep, finishedOnboarding } = useOnboardingContext();
    return finishedOnboarding === false ? (
      <FabChecklist isMobile={isMobile} currentStep={currentOnboardingStep} />
    ) : null;
  };

  const orgInfo = useOrgInfo();
  const {
    user: { userIdentityService },
  } = useUser();

  const isBillingPriviledged = userIdentityService.hasVmGlobalRule('manage-billing');
  const expiredDate = orgInfo?.dateDeactivated;
  const remainingDays = expiredDate
    ? (() => {
        try {
          const { days } = Interval.fromDateTimes(DateTime.local(), expiredDate).toDuration('days');
          return Number.isFinite(days) ? Math.round(days) : undefined;
        } catch {
          return undefined;
        }
      })()
    : undefined;

  return (
    <MuiAppBar
      position='relative'
      style={{
        backgroundColor: '#423951',
        color: '#ffffff',
        boxShadow: 'none',
      }}
    >
      <Toolbar style={{ paddingLeft: 24, paddingRight: 8, minHeight: theme.appShell.menu.height }}>
        {isMobile || !isDrawerOpen ? (
          <IconButton
            color='inherit'
            onClick={() => {
              toggleDrawerOpen();
            }}
            style={{ marginLeft: '-16px' }}
          >
            <MenuIcon />
          </IconButton>
        ) : null}
        <CampfireHeader condensed />

        <div style={{ flex: 1 }} />

        <FabChecklistWrapper />
        <Box display='flex' pl='12px' alignItems='center' style={{ gap: 12 }}>
          <Box>
            {isBillingPriviledged && expiredDate && (
              <Button
                variant='contained'
                startIcon={<AccessTimeOutlined style={{ marginBottom: '2px' }} />}
                color='primary'
                style={{ color: 'white', textTransform: 'none', fontSize: '13px' }}
                onClick={() => setUpgradeDialogOpen(true)}
              >
                {pluralize('days', remainingDays, true)} left
              </Button>
            )}
          </Box>
          <AvailabilityCenter />
          <AppBarAvatarMenu />
        </Box>
      </Toolbar>
      {remainingDays && (
        <TrialUpgradeDialog
          open={upgradeDialogOpen}
          onClose={() => setUpgradeDialogOpen(false)}
          remainingDays={remainingDays}
        />
      )}
    </MuiAppBar>
  );
};

export const AppBar = CampfireAppBar;
